/* eslint-disable max-lines */

import { Alert, AlertItem, Button } from '@vmware-react/clarity';
import {
  DCheckboxField,
  DFData,
  DFields,
  DFormState,
  DInputField,
  DSelectField,
  DynamicForm
} from '@vmware-react/vrlcm';
import React, { useEffect, useState } from 'react';
import { SettingsApi, PasswordApi } from 'vrlcm-gui-api';
import { IP_OR_FQDN_REGEX } from 'vrlcm-gui-constants';
import { Password, PasswordData, Proxy, ProxyDetail } from 'vrlcm-gui-types';
import { l10n } from '../with-app-root/i18n/i18nUtil';
import { CenterAlignedSpinner } from '../decorated-elements';

interface Props {
  onProxyDetailsChange?: (proxy: Proxy) => void;
  onAddPassword: () => void;
  passwords: Password[];
}

const ProxyConfiguration = (props: Props) => {
  const { onProxyDetailsChange, onAddPassword, passwords } = props;
  const initialValue: Proxy = {
    proxyEnabled: false,
    hostName: '',
    port: 0,
    password: '',
    proxyEnabledInContentMgmt: false,
    noProxyList: ''
  };
  const [valid, setValid] = useState(false);
  const [hasErrorMessage, setHasErrorMessage] = useState('');
  const [success, setSuccess] = useState(false);
  const [value, setValue] = useState<Proxy>(initialValue);

  const proxyEnabledFields: DFields = [
    {
      name: 'proxyEnabled',
      type: 'checkbox',
      required: true,
      disabled: value && value.proxyEnabledInContentMgmt,
      initialValue: value.proxyEnabled,
      label: l10n('components.proxyConfiguration.configureProxy'),
      attrs: {
        'aria-label': l10n('components.proxyConfiguration.configureProxy')
      }
    } as DCheckboxField
  ];

  const proxyDetailsFields: DFields = [
    {
      name: 'hostName',
      type: 'text',
      initialValue: value.hostName !== null ? value.hostName : '',
      placeholder: l10n('components.proxyConfiguration.hostNamePlaceholder'),
      label: l10n('components.proxyConfiguration.hostName'),
      required: true,
      autoComplete: false,
      validationRules: {
        type: 'string',
        rules: [
          {
            name: 'required',
            message: l10n('form.input.isRequired', [
              l10n('components.proxyConfiguration.hostName')
            ])
          },
          {
            name: 'matches',
            value: IP_OR_FQDN_REGEX,
            message: l10n('components.proxyConfiguration.invalidHostName')
          }
        ]
      }
    } as DInputField,
    {
      name: 'port',
      type: 'number',
      required: true,
      initialValue: value.port.toString(),
      placeholder: l10n('components.proxyConfiguration.portNumberPlaceholder'),
      label: l10n('components.proxyConfiguration.portNumber'),
      autoComplete: false,
      validationRules: {
        type: 'number',
        rules: [
          {
            name: 'required',
            message: l10n('form.input.isRequired', [
              l10n('components.proxyConfiguration.portNumber')
            ])
          },
          {
            name: 'min',
            message: l10n('form.greaterThanOrEqualTo', [
              l10n('components.proxyConfiguration.portNumber'),
              1
            ]),
            value: 1
          }
        ]
      }
    } as DInputField,
    {
      name: 'password',
      type: 'searchable',
      helperText: l10n('passwordHelperText'),
      helperTextPosition: 'after-label',
      initialValue:
        value.password !== null && value.password !== undefined
          ? value.password
          : '',
      options: passwords.map(password => ({
        textLabel: password.alias,
        value: `locker:password:${password.vmid}:${password.alias}`
      })),
      placeholder: l10n('components.proxyConfiguration.passwordPlaceholder'),
      label: l10n('components.proxyConfiguration.password'),
      buttonClickHandler: onAddPassword,
      autoComplete: false,
      validationRules: {
        type: 'string',
        rules: []
      }
    } as DSelectField,
    {
      name: 'noProxyList',
      type: 'text',
      initialValue: value.noProxyList !== null ? value.noProxyList : '',
      placeholder: l10n('lcops.settings.proxy.enterExclusionList'),
      label: l10n('lcops.settings.proxy.exclusionList'),
      required: false,
      autoComplete: false,
      helperText: l10n('lcops.settings.proxy.exclusionListNoProxy'),
      helperTextPosition: 'after-label',
      helperTextToolTipConfig: {
        size: 'sm',
        position: 'top-right'
      }
    } as DInputField
  ];

  const ProxyData: DFData = {
    fields: proxyDetailsFields,
    orientation: 'horizontal'
  };
  const ProxyEnabledData: DFData = {
    fields: proxyEnabledFields,
    orientation: 'horizontal'
  };

  const [loading, setLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateHasErrorMessage, setUpdateHasErrorMessage] = useState('');
  const [configureProxy, setConfigureProxy] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const onProxyDataChange = (next: DFormState) => {
    setValid(next.isValid);
    setValue(Object.assign(value, next.values));
  };
  const onProxyEnabledChange = (next: DFormState) => {
    if (next.values.proxyEnabled) {
      setConfigureProxy(true);
      setValue(Object.assign(value, next.values));
    } else {
      setConfigureProxy(false);
      setValid(true);
      setValue(Object.assign(value, initialValue));
    }
  };
  useEffect(() => {
    setSuccess(false);
    setHasErrorMessage('');
    setLoading(true);
    SettingsApi.GetProxy()
      .then(res => {
        setValue(res);
        onProxyDetailsChange?.(res);

        setSuccess(true);
        setLoading(false);
      })
      .catch(err => {
        setHasErrorMessage(err.response.data.message);
        setSuccess(false);
        setLoading(false);
      });
  }, []);
  const onSubmit = (password: PasswordData) => {
    const body: ProxyDetail = {
      proxyEnabled: value.proxyEnabled,
      hostName: value.hostName,
      port: value.port,
      password: value.password,
      proxyEnabledInContentMgmt: value.proxyEnabledInContentMgmt,
      userName: password.userName,
      noProxyList: value.noProxyList
    };
    onUpdateProxy(body);
  };
  const onUpdateProxy = (proxyDetail: ProxyDetail) => {
    setUpdateSuccess(false);
    setUpdateHasErrorMessage('');
    SettingsApi.UpdateProxy(proxyDetail)
      .then(res => {
        onProxyDetailsChange?.(res);
        setUpdateSuccess(true);
        setSubmitLoading(false);
      })
      .catch(err => {
        setUpdateHasErrorMessage(err.response.data.message);
        setUpdateSuccess(false);
        setSubmitLoading(false);
      });
  };
  const fetchPasswordDetails = () => {
    if (value && value.password) {
      setHasErrorMessage('');
      setSubmitLoading(true);
      const vmid = value && value.password.split(':')[2];
      PasswordApi.getPasswordByVmid(vmid)
        .then(res => {
          onSubmit(res);
        })
        .catch(err => {
          setSubmitLoading(false);
          setHasErrorMessage(err.response.data.message);
        });
    } else {
      const body: ProxyDetail = {
        proxyEnabled: value.proxyEnabled,
        hostName: value.hostName,
        proxyEnabledInContentMgmt: value.proxyEnabledInContentMgmt,
        port: value.port,
        noProxyList: value.noProxyList,
        password: '',
        userName: ''
      };
      onUpdateProxy(body);
    }
  };

  return (
    <div>
      <div className="section-title">
        {l10n('components.proxyConfiguration.title')}
      </div>
      {updateSuccess ? (
        <Alert
          type="success"
          ariaLiveMsg={l10n('components.proxyConfiguration.updateSuccess')}
          items={[
            <AlertItem>
              {l10n('components.proxyConfiguration.updateSuccess')}
            </AlertItem>
          ]}
        />
      ) : null}
      {updateHasErrorMessage !== '' ? (
        <Alert
          type="danger"
          ariaLiveMsg={updateHasErrorMessage}
          items={[<AlertItem>{updateHasErrorMessage}</AlertItem>]}
        />
      ) : null}
      {hasErrorMessage !== '' ? (
        <Alert
          type="danger"
          ariaLiveMsg={hasErrorMessage}
          items={[<AlertItem>{hasErrorMessage}</AlertItem>]}
        />
      ) : null}
      {configureProxy ? (
        <Alert
          type="info"
          closable={true}
          items={[
            <AlertItem>
              <span
                dangerouslySetInnerHTML={{
                  __html: l10n('components.proxyConfiguration.whitelistAlert')
                }}
              ></span>
            </AlertItem>
          ]}
        />
      ) : null}
      {value && value.proxyEnabledInContentMgmt ? (
        <Alert
          type="warning"
          ariaLiveMsg={l10n(
            'components.proxyConfiguration.disableNotAllowedText'
          )}
          items={[
            <AlertItem>
              <span
                dangerouslySetInnerHTML={{
                  __html: l10n(
                    'components.proxyConfiguration.disableNotAllowedHTML'
                  )
                }}
              ></span>
            </AlertItem>
          ]}
        />
      ) : null}
      <p>{l10n('lcops.settings.proxy.proxyDetails.configureProxyInfo')}</p>
      <div className="vrlcm-form">
        {success ? (
          <div className="form-width">
            <DynamicForm
              data={ProxyEnabledData}
              debugInfo={false}
              onChange={onProxyEnabledChange}
              reqMsg={l10n('form.requiredFields')}
            />
            {configureProxy ? (
              <DynamicForm
                data={ProxyData}
                debugInfo={false}
                onChange={onProxyDataChange}
                reqMsg={l10n('form.requiredFields')}
              />
            ) : null}
          </div>
        ) : null}
        {loading ? (
          <CenterAlignedSpinner size="sm" text={l10n('loading')} />
        ) : null}

        <Button
          disabled={!valid || submitLoading}
          onClick={fetchPasswordDetails}
          class="primary"
          size="sm"
        >
          {submitLoading ? (
            <span className="spinner spinner-inline m-r-0-3">
              {l10n('loading')}
            </span>
          ) : null}
          <span>{l10n('save')}</span>
        </Button>
      </div>
    </div>
  );
};
export default ProxyConfiguration;
